// ─────────────────────────────────────────────────────────────────────────────
//  Mathematics → Sequences & Series (JEE Main + Advanced)
// ─────────────────────────────────────────────────────────────────────────────

function buildAPGPVisual() {
  const W = 480, H = 220;
  const baseY = 180;
  // AP: linear growth a=2, d=3 -> 2,5,8,11,14
  const ap = [2, 5, 8, 11, 14];
  // GP: geometric a=2, r=1.5 -> 2, 3, 4.5, 6.75, 10.125
  const gp = [2, 3, 4.5, 6.75, 10.125];
  const colW = 36, gap = 8;
  const groupW = ap.length * (colW + gap);
  const drawBars = (xOff, arr, color, label) => {
    return `<g transform="translate(${xOff},0)">
      ${arr.map((v, i) => {
        const h = v * 9;
        const x = i * (colW + gap), y = baseY - h;
        return `<rect x="${x}" y="${y}" width="${colW}" height="${h}" rx="3" fill="${color}" opacity="0.85"/>
                <text x="${x+colW/2}" y="${baseY+14}" font-size="11" fill="#374151" text-anchor="middle">T${i+1}</text>
                <text x="${x+colW/2}" y="${y-4}" font-size="11" fill="#1f2937" text-anchor="middle">${v}</text>`;
      }).join('')}
      <text x="${groupW/2}" y="${baseY+34}" font-size="13" fill="${color}" text-anchor="middle" font-weight="700">${label}</text>
    </g>`;
  };
  return `
<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
  ${drawBars(10, ap, '#4338ca', 'AP (d = 3)')}
  ${drawBars(W/2 + 10, gp, '#dc2626', 'GP (r = 1.5)')}
  <line x1="0" y1="${baseY}" x2="${W}" y2="${baseY}" stroke="#374151"/>
</svg>`;
}

function buildAMGMHMVisual() {
  // number line with positions of A, G, H for two positive numbers
  const W = 460, H = 140;
  const a = 1, b = 9; // example
  const Am = (a + b) / 2; // 5
  const Gm = Math.sqrt(a * b); // 3
  const Hm = (2 * a * b) / (a + b); // 1.8
  const xMin = 0, xMax = 10, scale = (W - 60) / (xMax - xMin);
  const px = v => 30 + (v - xMin) * scale;
  return `
<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="80" x2="${W-20}" y2="80" stroke="#374151" stroke-width="1.5"/>
  ${[0,2,4,6,8,10].map(t => `<line x1="${px(t)}" y1="76" x2="${px(t)}" y2="84" stroke="#9ca3af"/><text x="${px(t)}" y="100" font-size="10" fill="#6b7280" text-anchor="middle">${t}</text>`).join('')}
  <circle cx="${px(a)}" cy="80" r="4" fill="#1f2937"/>
  <circle cx="${px(b)}" cy="80" r="4" fill="#1f2937"/>
  <text x="${px(a)}" y="68" font-size="11" fill="#1f2937" text-anchor="middle">a = 1</text>
  <text x="${px(b)}" y="68" font-size="11" fill="#1f2937" text-anchor="middle">b = 9</text>
  <circle cx="${px(Hm)}" cy="80" r="5" fill="#dc2626"/>
  <circle cx="${px(Gm)}" cy="80" r="5" fill="#10b981"/>
  <circle cx="${px(Am)}" cy="80" r="5" fill="#4338ca"/>
  <text x="${px(Hm)}" y="40" font-size="12" fill="#b91c1c" text-anchor="middle" font-weight="600">HM = 1.8</text>
  <text x="${px(Gm)}" y="55" font-size="12" fill="#047857" text-anchor="middle" font-weight="600">GM = 3</text>
  <text x="${px(Am)}" y="40" font-size="12" fill="#3730a3" text-anchor="middle" font-weight="600">AM = 5</text>
  <text x="${W/2}" y="125" font-size="12" fill="#6b7280" text-anchor="middle">For positive a, b:  HM ≤ GM ≤ AM</text>
</svg>`;
}

const FIG_AP_GP = buildAPGPVisual();
const FIG_AM_GM_HM = buildAMGMHMVisual();
const bi = (english, hindi) => ({ english, hindi });

const chapter = {
  id: 'sequences-and-series',
  slug: 'sequences-and-series',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: bi('Sequences & Series', 'अनुक्रम और श्रेणी'),
  estReadMinutes: 50,
  difficulty: bi('Medium', 'मध्यम'),
  weightage: bi('2 questions in JEE Main, 1 in Advanced', 'JEE Main में 2 प्रश्न, JEE Advanced में 1 प्रश्न'),
  syllabusTags: ['Algebra', 'JEE-Main', 'JEE-Advanced', 'Class-11'],

  sections: [
    {
      id: 'intro',
      title: bi('Sequence vs Series', 'अनुक्रम बनाम श्रेणी'),
      blocks: [
        {
          type: 'definition',
          term: bi('Sequence', 'अनुक्रम'),
          body: bi(
            'A **sequence** is a function whose domain is the set of natural numbers (or a subset of it). The image $a_1, a_2, a_3, \\ldots$ is the sequence; $a_n$ is the **n-th term** or **general term**.',
            'एक **अनुक्रम** ऐसा फलन है जिसका परिभाषा-क्षेत्र प्राकृतिक संख्याओं का समुच्चय (या उसका कोई उपसमुच्चय) होता है। $a_1, a_2, a_3, \\ldots$ उसके पद हैं; $a_n$ को **n-वाँ पद** या **सामान्य पद** कहते हैं।'
          ),
        },
        {
          type: 'definition',
          term: bi('Series', 'श्रेणी'),
          body: bi(
            'A **series** is the sum of terms of a sequence: $S = a_1 + a_2 + a_3 + \\ldots$. A **finite series** has finitely many terms; otherwise it is **infinite**.',
            'एक **श्रेणी** किसी अनुक्रम के पदों का योग है: $S = a_1 + a_2 + a_3 + \\ldots$। **ससीम श्रेणी** में पदों की संख्या ससीम होती है; अन्यथा वह **अनंत** होती है।'
          ),
        },
        {
          type: 'note',
          text: bi(
            'Sequences may be **finite, infinite, increasing, decreasing, bounded** or **unbounded**.',
            'अनुक्रम **ससीम, अनंत, वर्धमान, अवर्धमान, परिबद्ध** या **अपरिबद्ध** हो सकते हैं।'
          ),
        },
      ],
    },

    {
      id: 'ap',
      title: bi('Arithmetic Progression (AP)', 'अंकगणितीय प्रगति (AP)'),
      blocks: [
        {
          type: 'definition',
          term: bi('AP', 'AP'),
          body: bi(
            'A sequence is in **arithmetic progression** if the difference between consecutive terms is constant. This common difference is denoted $d$.',
            'यदि किसी अनुक्रम में क्रमागत पदों का अंतर नियत हो, तो वह **अंकगणितीय प्रगति** कहलाता है। इस नियत अंतर को $d$ से दर्शाते हैं।'
          ),
        },
        { type: 'equation', latex: 'a,\\ a+d,\\ a+2d,\\ a+3d,\\ \\ldots' },
        { type: 'equation', latex: 'T_n = a + (n-1)d', label: bi('n-th term of AP', 'AP का n-वाँ पद') },
        { type: 'equation', latex: 'S_n = \\dfrac{n}{2}\\bigl[2a + (n-1)d\\bigr] = \\dfrac{n}{2}(a + l)', label: bi('Sum of first n terms; l = last term', 'प्रथम n पदों का योग; l = अंतिम पद') },
        {
          type: 'theorem',
          name: bi('Term-from-end', 'अंत से पद'),
          statement: bi(
            'The $r$-th term from the end of an AP with $n$ terms is the $(n - r + 1)$-th term from the beginning, i.e. $T_{n-r+1} = a + (n - r)d$.',
            '$n$ पदों वाले AP में अंत से $r$-वाँ पद, प्रारंभ से $(n-r+1)$-वाँ पद होता है, अर्थात $T_{n-r+1} = a + (n-r)d$।'
          ),
        },
        {
          type: 'tip',
          text: bi(
            '**Symmetric tricks** for choosing AP terms:\n• 3 terms: $a-d, a, a+d$ (sum $= 3a$).\n• 4 terms: $a-3d, a-d, a+d, a+3d$ (common difference $2d$).\n• 5 terms: $a-2d, a-d, a, a+d, a+2d$.',
            'AP के पद चुनने की **सममित युक्ति**:\n• 3 पद: $a-d, a, a+d$ (योग $= 3a$)।\n• 4 पद: $a-3d, a-d, a+d, a+3d$ (समान अंतर $2d$)।\n• 5 पद: $a-2d, a-d, a, a+d, a+2d$।'
          ),
        },
        {
          type: 'figure',
          svg: FIG_AP_GP,
          caption: bi('Visual contrast: AP (left) grows linearly with constant gap; GP (right) grows multiplicatively.', 'दृश्य तुलना: AP (बाएँ) नियत अंतर के साथ रैखिक रूप से बढ़ता है; GP (दाएँ) गुणात्मक रूप से बढ़ता है।'),
        },
        {
          type: 'list',
          items: [
            bi('If a constant is added/subtracted/multiplied to every term of an AP, the result is still an AP.', 'यदि AP के प्रत्येक पद में कोई नियतांक जोड़ा/घटाया/गुणा किया जाए, तब भी परिणाम AP ही रहता है।'),
            bi('If terms of two APs are added, the sum is an AP.', 'दो AP के संगत पदों का योग भी AP होता है।'),
            bi('Three numbers $a, b, c$ are in AP iff $2b = a + c$.', 'तीन संख्याएँ $a, b, c$ AP में होंगी तभी और तभी जब $2b = a + c$ हो।'),
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: bi('Find the sum of all 3-digit natural numbers divisible by 7.', '7 से विभाज्य सभी 3-अंकीय प्राकृतिक संख्याओं का योग ज्ञात करें।'),
          solution: bi('First such number = 105, last = 994, $d = 7$. $n = (994 - 105)/7 + 1 = 128$. $S = \\tfrac{128}{2}(105 + 994) = 64 \\times 1099 = 70336$.', 'पहली संख्या 105 और अंतिम 994 है, $d = 7$। अतः $n = (994 - 105)/7 + 1 = 128$। इसलिए $S = \\tfrac{128}{2}(105 + 994) = 64 \\times 1099 = 70336$।'),
        },
      ],
    },

    {
      id: 'gp',
      title: bi('Geometric Progression (GP)', 'ज्यामितीय प्रगति (GP)'),
      blocks: [
        {
          type: 'definition',
          term: bi('GP', 'GP'),
          body: bi(
            'A sequence is in **geometric progression** if the ratio of consecutive terms is constant. This common ratio is denoted $r$ ($r \\ne 0$).',
            'यदि किसी अनुक्रम में क्रमागत पदों का अनुपात नियत हो, तो वह **ज्यामितीय प्रगति** कहलाता है। इस नियत अनुपात को $r$ ($r \\ne 0$) से दर्शाते हैं।'
          ),
        },
        { type: 'equation', latex: 'a,\\ ar,\\ ar^2,\\ ar^3,\\ \\ldots' },
        { type: 'equation', latex: 'T_n = a r^{n-1}', label: bi('n-th term of GP', 'GP का n-वाँ पद') },
        { type: 'equation', latex: 'S_n = a\\,\\dfrac{1 - r^n}{1 - r},\\quad r \\ne 1', label: bi('Sum of first n terms', 'प्रथम n पदों का योग') },
        { type: 'equation', latex: 'S_\\infty = \\dfrac{a}{1 - r},\\quad |r| < 1', label: bi('Sum of infinite GP', 'अनंत GP का योग') },
        {
          type: 'tip',
          text: bi(
            '**Symmetric tricks**:\n• 3 terms: $a/r, a, ar$ (product $= a^3$).\n• 4 terms: $a/r^3, a/r, ar, ar^3$ (common ratio $r^2$).',
            '**सममित युक्ति**:\n• 3 पद: $a/r, a, ar$ (गुणनफल $= a^3$)।\n• 4 पद: $a/r^3, a/r, ar, ar^3$ (समान अनुपात $r^2$)।'
          ),
        },
        {
          type: 'list',
          items: [
            bi('Three numbers $a, b, c$ are in GP iff $b^2 = ac$.', 'तीन संख्याएँ $a, b, c$ GP में होंगी तभी और तभी जब $b^2 = ac$ हो।'),
            bi('If each term of a GP is multiplied by a non-zero constant, the result is a GP.', 'GP के प्रत्येक पद को किसी अशून्य नियतांक से गुणा करने पर प्राप्त अनुक्रम भी GP होता है।'),
            bi('Reciprocals of GP terms form a GP with ratio $1/r$.', 'GP के पदों के व्युत्क्रम, $1/r$ अनुपात वाला GP बनाते हैं।'),
            bi('For an infinite GP to converge, $|r| < 1$.', 'अनंत GP के अभिसरण के लिए $|r| < 1$ होना आवश्यक है।'),
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: bi('Find the sum of the infinite series $1 + \\tfrac{1}{2} + \\tfrac{1}{4} + \\tfrac{1}{8} + \\ldots$', 'अनंत श्रेणी $1 + \\tfrac{1}{2} + \\tfrac{1}{4} + \\tfrac{1}{8} + \\ldots$ का योग ज्ञात करें।'),
          solution: bi('$a = 1$, $r = 1/2$, $|r| < 1$, so $S_\\infty = \\dfrac{1}{1 - 1/2} = 2$.', '$a = 1$, $r = 1/2$, और $|r| < 1$ है, अतः $S_\\infty = \\dfrac{1}{1 - 1/2} = 2$।'),
        },
      ],
    },

    {
      id: 'hp',
      title: bi('Harmonic Progression (HP)', 'हरात्मक प्रगति (HP)'),
      blocks: [
        {
          type: 'definition',
          term: bi('HP', 'HP'),
          body: bi(
            'A sequence $a_1, a_2, a_3, \\ldots$ ($a_i \\ne 0$) is in **harmonic progression** if its reciprocals $1/a_1, 1/a_2, 1/a_3, \\ldots$ form an AP.',
            'यदि अनुक्रम $a_1, a_2, a_3, \\ldots$ ($a_i \\ne 0$) के व्युत्क्रम $1/a_1, 1/a_2, 1/a_3, \\ldots$ एक AP बनाते हों, तो वह **हरात्मक प्रगति** कहलाता है।'
          ),
        },
        {
          type: 'paragraph',
          text: bi(
            'There is no closed-form formula for $S_n$ of an HP in general; convert to AP via reciprocals.',
            'सामान्यतः HP के $S_n$ का कोई सरल बंद सूत्र नहीं होता; इसे व्युत्क्रम लेकर AP में बदलकर हल करते हैं।'
          ),
        },
        {
          type: 'example',
          number: 3,
          problem: bi('Find the 10th term of the HP $1, \\tfrac{1}{3}, \\tfrac{1}{5}, \\tfrac{1}{7}, \\ldots$', 'HP $1, \\tfrac{1}{3}, \\tfrac{1}{5}, \\tfrac{1}{7}, \\ldots$ का 10वाँ पद ज्ञात करें।'),
          solution: bi('Reciprocals are $1, 3, 5, 7, \\ldots$ — an AP with $a = 1, d = 2$. $T_{10}^{AP} = 1 + 9(2) = 19$. So $T_{10}^{HP} = 1/19$.', 'व्युत्क्रम $1, 3, 5, 7, \\ldots$ हैं, जो $a = 1, d = 2$ वाला AP है। $T_{10}^{AP} = 1 + 9(2) = 19$। अतः $T_{10}^{HP} = 1/19$।'),
        },
      ],
    },

    {
      id: 'means',
      title: bi('AM, GM, HM and the Inequality', 'AM, GM, HM और असमता'),
      blocks: [
        {
          type: 'definition',
          term: bi('Means of two positive numbers', 'दो धनात्मक संख्याओं के माध्य'),
          body: bi(
            'For $a, b > 0$: $\\;\\;AM = \\dfrac{a+b}{2}, \\quad GM = \\sqrt{ab}, \\quad HM = \\dfrac{2ab}{a+b}.$',
            '$a, b > 0$ के लिए: $\\;\\;AM = \\dfrac{a+b}{2}, \\quad GM = \\sqrt{ab}, \\quad HM = \\dfrac{2ab}{a+b}$।'
          ),
        },
        {
          type: 'theorem',
          name: bi('AM–GM–HM Inequality', 'AM–GM–HM असमता'),
          statement: bi(
            'For positive reals, $\\;AM \\ge GM \\ge HM,\\;$ with equality iff all numbers are equal. Also $GM^2 = AM \\cdot HM$.',
            'धनात्मक वास्तविक संख्याओं के लिए $\\;AM \\ge GM \\ge HM\\;$ और समानता तभी होगी जब सभी संख्याएँ समान हों। साथ ही $GM^2 = AM \\cdot HM$।'
          ),
          proof: bi(
            'For two positives, $AM - GM = \\tfrac{a+b}{2} - \\sqrt{ab} = \\tfrac{(\\sqrt a - \\sqrt b)^2}{2} \\ge 0$. Similar reasoning gives $GM \\ge HM$.',
            'दो धनात्मक संख्याओं के लिए $AM - GM = \\tfrac{a+b}{2} - \\sqrt{ab} = \\tfrac{(\\sqrt a - \\sqrt b)^2}{2} \\ge 0$। इसी प्रकार $GM \\ge HM$ सिद्ध होता है।'
          ),
        },
        {
          type: 'figure',
          svg: FIG_AM_GM_HM,
          caption: bi(
            'For a = 1 and b = 9: HM = 1.8, GM = 3, AM = 5. Notice GM² = 9 = 1.8 × 5 = AM·HM.',
            'a = 1 और b = 9 के लिए: HM = 1.8, GM = 3, AM = 5। ध्यान दें: GM² = 9 = 1.8 × 5 = AM·HM।'
          ),
        },
        {
          type: 'paragraph',
          text: bi(
            'For $n$ positive reals $a_1, \\ldots, a_n$, the inequality generalises:',
            '$n$ धनात्मक वास्तविक संख्याओं $a_1, \\ldots, a_n$ के लिए यह असमता सामान्यीकृत रूप में लिखी जाती है:'
          ),
        },
        { type: 'equation', latex: '\\dfrac{a_1 + a_2 + \\ldots + a_n}{n} \\ge \\sqrt[n]{a_1 a_2 \\cdots a_n} \\ge \\dfrac{n}{\\dfrac{1}{a_1} + \\dfrac{1}{a_2} + \\ldots + \\dfrac{1}{a_n}}' },
        {
          type: 'example',
          number: 4,
          problem: bi('If $a + b + c = 6$ and $a, b, c > 0$, find the maximum value of $abc$.', 'यदि $a + b + c = 6$ तथा $a, b, c > 0$ हों, तो $abc$ का अधिकतम मान ज्ञात करें।'),
          solution: bi('By AM–GM, $\\dfrac{a+b+c}{3} \\ge \\sqrt[3]{abc} \\Rightarrow 2 \\ge \\sqrt[3]{abc} \\Rightarrow abc \\le 8$. Maximum occurs at $a = b = c = 2$.', 'AM–GM से $\\dfrac{a+b+c}{3} \\ge \\sqrt[3]{abc} \\Rightarrow 2 \\ge \\sqrt[3]{abc} \\Rightarrow abc \\le 8$। अधिकतम मान $a = b = c = 2$ पर प्राप्त होता है।'),
        },
      ],
    },

    {
      id: 'arithmetico-geometric',
      title: bi('Arithmetico-Geometric Progression (AGP)', 'अंकगणितीय-ज्यामितीय प्रगति (AGP)'),
      blocks: [
        {
          type: 'paragraph',
          text: bi(
            'An AGP is the term-by-term product of an AP and a GP. General form: $a, (a+d)r, (a+2d)r^2, \\ldots$.',
            'AGP, AP और GP के संगत पदों के गुणन से बनता है। इसका सामान्य रूप: $a, (a+d)r, (a+2d)r^2, \\ldots$।'
          ),
        },
        { type: 'equation', latex: 'S_n = \\dfrac{a}{1-r} - \\dfrac{(a + (n-1)d)r^n}{1-r} + \\dfrac{dr(1 - r^{n-1})}{(1-r)^2},\\ r \\ne 1' },
        { type: 'equation', latex: 'S_\\infty = \\dfrac{a}{1 - r} + \\dfrac{dr}{(1-r)^2},\\quad |r| < 1' },
        {
          type: 'tip',
          text: bi(
            '**Standard trick**: write $S$, multiply both sides by $r$, subtract; the middle terms become a GP.',
            '**मानक युक्ति**: पहले $S$ लिखें, दोनों ओर $r$ से गुणा करें, फिर घटाएँ; बीच के पद GP की तरह सरल हो जाते हैं।'
          ),
        },
        {
          type: 'example',
          number: 5,
          problem: bi('Find $S = 1 + 2x + 3x^2 + 4x^3 + \\ldots$ for $|x| < 1$.', '$|x| < 1$ के लिए $S = 1 + 2x + 3x^2 + 4x^3 + \\ldots$ का मान ज्ञात करें।'),
          solution: bi('Multiply by $x$: $xS = x + 2x^2 + 3x^3 + \\ldots$. Subtract: $(1 - x)S = 1 + x + x^2 + \\ldots = \\dfrac{1}{1-x}$. So $S = \\dfrac{1}{(1-x)^2}$.', '$x$ से गुणा करें: $xS = x + 2x^2 + 3x^3 + \\ldots$। घटाने पर $(1 - x)S = 1 + x + x^2 + \\ldots = \\dfrac{1}{1-x}$। अतः $S = \\dfrac{1}{(1-x)^2}$।'),
        },
      ],
    },

    {
      id: 'special-sums',
      title: bi('Sums of Special Series', 'विशेष श्रेणियों के योग'),
      blocks: [
        {
          type: 'table',
          headers: ['Series', 'Sum'],
          rows: [
            ['$\\sum_{k=1}^{n} k$', '$\\dfrac{n(n+1)}{2}$'],
            ['$\\sum_{k=1}^{n} k^2$', '$\\dfrac{n(n+1)(2n+1)}{6}$'],
            ['$\\sum_{k=1}^{n} k^3$', '$\\left(\\dfrac{n(n+1)}{2}\\right)^2$'],
            ['$\\sum_{k=1}^{n} (2k-1)$', '$n^2$ (sum of first $n$ odd numbers)'],
            ['$\\sum_{k=1}^{n} 2k$', '$n(n+1)$'],
            ['$\\sum_{k=1}^{n} k(k+1)$', '$\\dfrac{n(n+1)(n+2)}{3}$'],
            ['$\\sum_{k=1}^{n} k(k+1)(k+2)$', '$\\dfrac{n(n+1)(n+2)(n+3)}{4}$'],
          ],
        },
        {
          type: 'tip',
          text: bi(
            '**Pattern**: $\\sum k(k+1)(k+2)\\cdots(k+r-1) = \\dfrac{n(n+1)(n+2)\\cdots(n+r)}{r+1}$. Memorise this telescoping pattern — saves time on JEE.',
            '**पैटर्न**: $\\sum k(k+1)(k+2)\\cdots(k+r-1) = \\dfrac{n(n+1)(n+2)\\cdots(n+r)}{r+1}$। इस टेलीस्कोपिंग पैटर्न को याद रखें, यह JEE में समय बचाता है।'
          ),
        },
        {
          type: 'example',
          number: 6,
          problem: bi('Find $\\sum_{k=1}^{n} k(k+1)$.', '$\\sum_{k=1}^{n} k(k+1)$ का मान ज्ञात करें।'),
          solution: bi('$\\sum k^2 + \\sum k = \\dfrac{n(n+1)(2n+1)}{6} + \\dfrac{n(n+1)}{2} = \\dfrac{n(n+1)(n+2)}{3}$.', '$\\sum k^2 + \\sum k = \\dfrac{n(n+1)(2n+1)}{6} + \\dfrac{n(n+1)}{2} = \\dfrac{n(n+1)(n+2)}{3}$।'),
        },
      ],
    },

    {
      id: 'method-of-differences',
      title: bi('Method of Differences (Telescoping)', 'अंतर विधि (टेलीस्कोपिंग)'),
      blocks: [
        {
          type: 'paragraph',
          text: bi(
            'When $T_n$ can be written as $f(n) - f(n-1)$ (or similar), $\\sum_{k=1}^{n} T_k$ telescopes:',
            'जब $T_n$ को $f(n) - f(n-1)$ (या समान रूप) में लिखा जा सके, तब $\\sum_{k=1}^{n} T_k$ टेलीस्कोपिंग रूप ले लेता है:'
          ),
        },
        { type: 'equation', latex: '\\sum_{k=1}^{n} \\bigl[f(k) - f(k-1)\\bigr] = f(n) - f(0)' },
        {
          type: 'example',
          number: 7,
          problem: bi('Find $\\sum_{k=1}^{n} \\dfrac{1}{k(k+1)}$.', '$\\sum_{k=1}^{n} \\dfrac{1}{k(k+1)}$ का मान ज्ञात करें।'),
          solution: bi('Use partial fractions: $\\dfrac{1}{k(k+1)} = \\dfrac{1}{k} - \\dfrac{1}{k+1}$. Sum telescopes: $1 - \\dfrac{1}{n+1} = \\dfrac{n}{n+1}$.', 'आंशिक भिन्नों का प्रयोग करें: $\\dfrac{1}{k(k+1)} = \\dfrac{1}{k} - \\dfrac{1}{k+1}$। योग टेलीस्कोप होगा: $1 - \\dfrac{1}{n+1} = \\dfrac{n}{n+1}$।'),
        },
        {
          type: 'example',
          number: 8,
          problem: bi('Find the sum to infinity of $\\sum_{k=1}^{\\infty} \\dfrac{1}{k(k+1)(k+2)}$.', '$\\sum_{k=1}^{\\infty} \\dfrac{1}{k(k+1)(k+2)}$ का अनंत तक योग ज्ञात करें।'),
          solution: bi('Decompose: $\\dfrac{1}{k(k+1)(k+2)} = \\dfrac{1}{2}\\left[\\dfrac{1}{k(k+1)} - \\dfrac{1}{(k+1)(k+2)}\\right]$. Telescopes to $\\dfrac{1}{2}\\cdot\\dfrac{1}{1\\cdot 2} = \\dfrac{1}{4}$.', 'विघटित करें: $\\dfrac{1}{k(k+1)(k+2)} = \\dfrac{1}{2}\\left[\\dfrac{1}{k(k+1)} - \\dfrac{1}{(k+1)(k+2)}\\right]$। टेलीस्कोपिंग से योग $\\dfrac{1}{2}\\cdot\\dfrac{1}{1\\cdot 2} = \\dfrac{1}{4}$ मिलता है।'),
        },
      ],
    },
  ],

  keyFormulas: [
    { latex: 'T_n^{AP} = a + (n-1)d', desc: bi('AP n-th term', 'AP का n-वाँ पद') },
    { latex: 'S_n^{AP} = \\tfrac{n}{2}[2a + (n-1)d]', desc: bi('AP sum', 'AP का योग') },
    { latex: 'T_n^{GP} = ar^{n-1}', desc: bi('GP n-th term', 'GP का n-वाँ पद') },
    { latex: 'S_n^{GP} = a\\,\\tfrac{1-r^n}{1-r}', desc: bi('GP sum (r ≠ 1)', 'GP का योग (r ≠ 1)') },
    { latex: 'S_\\infty^{GP} = \\tfrac{a}{1-r},\\ |r|<1', desc: bi('Infinite GP', 'अनंत GP') },
    { latex: 'AM \\ge GM \\ge HM', desc: bi('Mean inequality', 'माध्य असमता') },
    { latex: '\\sum k = \\tfrac{n(n+1)}{2}', desc: bi('Sum of n naturals', 'n प्राकृतिक संख्याओं का योग') },
    { latex: '\\sum k^2 = \\tfrac{n(n+1)(2n+1)}{6}', desc: bi('Sum of squares', 'वर्गों का योग') },
    { latex: '\\sum k^3 = \\left(\\tfrac{n(n+1)}{2}\\right)^2', desc: bi('Sum of cubes', 'घनों का योग') },
  ],

  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: bi('AP', 'AP'),
      problem: bi('The sum of the first 20 terms of an AP whose first term is $5$ and common difference $4$ is:', 'एक AP का प्रथम पद $5$ और समान अंतर $4$ है। उसके पहले 20 पदों का योग है:'),
      options: ['$840$', '$860$', '$880$', '$900$'],
      answer: bi('(A) $860$', '(A) $860$'),
      solution: bi('$S_{20} = \\tfrac{20}{2}[2(5) + 19(4)] = 10 \\cdot 86 = 860$.', '$S_{20} = \\tfrac{20}{2}[2(5) + 19(4)] = 10 \\cdot 86 = 860$।'),
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: bi('GP', 'GP'),
      problem: bi('If $1, x, y, z, 16$ are in GP, find $x + y + z$.', 'यदि $1, x, y, z, 16$ GP में हों, तो $x + y + z$ ज्ञात करें।'),
      answer: bi('$14$ (with $r = 2$).', '$14$ (जहाँ $r = 2$)'),
      solution: bi('Common ratio $r$ satisfies $r^4 = 16 \\Rightarrow r = 2$. Hence $x = 2, y = 4, z = 8$. Sum $= 14$.', 'समान अनुपात $r$ के लिए $r^4 = 16 \\Rightarrow r = 2$। अतः $x = 2, y = 4, z = 8$ और योग $= 14$।'),
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: bi('AGP', 'AGP'),
      problem: bi('Sum to $n$ terms of $1 + \\tfrac{4}{5} + \\tfrac{7}{25} + \\tfrac{10}{125} + \\ldots$', '$1 + \\tfrac{4}{5} + \\tfrac{7}{25} + \\tfrac{10}{125} + \\ldots$ का $n$ पदों तक योग ज्ञात करें।'),
      answer: bi('$\\dfrac{35}{16} - \\dfrac{(15n - 35) }{16 \\cdot 5^{n-1}}$ (standard AGP form).', '$\\dfrac{35}{16} - \\dfrac{(15n - 35)}{16 \\cdot 5^{n-1}}$ (मानक AGP रूप)'),
      solution: bi('Recognise as AGP with AP $1, 4, 7, \\ldots$ and GP $1, 1/5, 1/25, \\ldots$. Apply the AGP formula.', 'इसे AGP के रूप में पहचानें, जहाँ AP $1, 4, 7, \\ldots$ और GP $1, 1/5, 1/25, \\ldots$ है। फिर AGP का सूत्र लगाएँ।'),
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: bi('AM-GM', 'AM-GM'),
      problem: bi('If $a, b > 0$ and $a + b = 4$, the minimum value of $\\dfrac{1}{a} + \\dfrac{1}{b}$ is:', 'यदि $a, b > 0$ तथा $a + b = 4$, तो $\\dfrac{1}{a} + \\dfrac{1}{b}$ का न्यूनतम मान क्या है?'),
      answer: bi('$1$', '$1$'),
      solution: bi('AM-HM: $\\dfrac{a+b}{2} \\ge \\dfrac{2ab}{a+b} \\Rightarrow ab \\le 4$. So $\\dfrac{1}{a}+\\dfrac{1}{b} = \\dfrac{a+b}{ab} = \\dfrac{4}{ab} \\ge 1$. Min at $a = b = 2$.', 'AM-HM से $\\dfrac{a+b}{2} \\ge \\dfrac{2ab}{a+b} \\Rightarrow ab \\le 4$। अतः $\\dfrac{1}{a}+\\dfrac{1}{b} = \\dfrac{a+b}{ab} = \\dfrac{4}{ab} \\ge 1$। न्यूनतम मान $a = b = 2$ पर मिलता है।'),
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: bi('Method of differences', 'अंतर विधि'),
      problem: bi('Find $\\sum_{k=1}^{n} \\dfrac{k}{(k+1)!}$.', '$\\sum_{k=1}^{n} \\dfrac{k}{(k+1)!}$ ज्ञात करें।'),
      answer: bi('$1 - \\dfrac{1}{(n+1)!}$', '$1 - \\dfrac{1}{(n+1)!}$'),
      solution: bi('$\\dfrac{k}{(k+1)!} = \\dfrac{(k+1)-1}{(k+1)!} = \\dfrac{1}{k!} - \\dfrac{1}{(k+1)!}$. Telescopes to $1 - \\tfrac{1}{(n+1)!}$.', '$\\dfrac{k}{(k+1)!} = \\dfrac{(k+1)-1}{(k+1)!} = \\dfrac{1}{k!} - \\dfrac{1}{(k+1)!}$। यह टेलीस्कोप होकर $1 - \\tfrac{1}{(n+1)!}$ देता है।'),
    },
  ],

  next: { slug: 'binomial-theorem', title: bi('Binomial Theorem', 'द्विपद प्रमेय') },
};

export default chapter;
